/**
 * Classroom Management (Multi-Class Support)
 *
 * Functions for managing teacher classrooms and student assignments.
 * Part of the accounts domain (split from accountHelpers.ts).
 *
 * Fase 2 additions (s82): joinCode system, classroom lookup by code,
 * joinClassroomByCode, regenerateJoinCode.
 */

import { AccountsState, Classroom, LearnerAccount, isTeacherAccount } from '../types';
import { linkTeacherToLearner } from './relationships';
import { generateRandomCode } from '../../utils/randomCode';

// ── ID & Code Generation ──

export function generateClassroomId(): string {
  const randomBytes = new Uint8Array(5);
  crypto.getRandomValues(randomBytes);
  const randomPart = Array.from(randomBytes, b => b.toString(36)).join('').substring(0, 7);
  return `class_${randomPart}_${Date.now().toString(36)}`;
}

/**
 * Lengte van de KLASCODE (leerling → klas).
 *
 * Blijft 6: deze code wordt op het bord geschreven en door dertig kinderen
 * overgetypt, dus elk extra teken kost hier wél iets. De bescherming zit bij
 * deze code niet in lengte maar in beperkte macht — lid worden van een klas, en
 * niets meer. Voor docentrechten bestaat een aparte, langere code (zie
 * `CO_TEACHER_CODE_LENGTH`).
 *
 * Openstaand (SHORT_CODE_SECURITY.md B-2): klascodes hebben nog geen einddatum,
 * dus een code op een werkblad van vorig schooljaar werkt nog.
 */
export const JOIN_CODE_LENGTH = 6;

/**
 * Voorbeeldklascode voor placeholders.
 *
 * Stond als `"Bijv. A1B2C3"` in het scherm — die code KAN NIET BESTAAN: `1`
 * zit niet in het ondubbelzinnige alfabet. Een voorbeeld dat een onmogelijke
 * vorm toont, is erger dan geen voorbeeld: het kind vergelijkt zijn eigen code
 * ermee en concludeert dat het de verkeerde heeft.
 */
export const JOIN_CODE_EXAMPLE = 'A4B2C3';

/**
 * Genereer een klascode. Delegeert naar de gedeelde generator (D-SCAN-2 D2-2).
 */
export function generateJoinCode(): string {
  return generateRandomCode(JOIN_CODE_LENGTH);
}

/**
 * Generate a unique join code that doesn't collide with existing codes.
 * Scans all classrooms across all teachers.
 */
export function generateUniqueJoinCode(state: AccountsState): string {
  // Collect all existing joinCodes from top-level classroom registry
  const existingCodes = new Set<string>(Object.values(state.classrooms).map(c => c.joinCode));
  let code = generateJoinCode();
  let attempts = 0;
  while (existingCodes.has(code) && attempts < 100) {
    code = generateJoinCode();
    attempts++;
  }
  return code;
}

/** Lengte van de co-docentcode. Bewust 8 en niet 6 — zie hieronder. */
export const CO_TEACHER_CODE_LENGTH = 8;

/**
 * Genereer een code waarmee een collega CO-DOCENT wordt.
 *
 * Dit is een ander artefact dan de joinCode, en dat is de hele bedoeling
 * (SHORT_CODE_SECURITY.md B-1): de joinCode wordt met de hele klas gedeeld en
 * mag daarom nooit docentrechten geven.
 *
 * 8 tekens uit hetzelfde alfabet van 29 ≈ 500 miljard combinaties, ruim 800×
 * meer dan 6 tekens. Deze code wordt niet aan dertig kinderen gedicteerd maar
 * één keer aan één collega doorgegeven, dus twee tekens extra kosten niets.
 *
 * Het lengteverschil is ook een structurele scheiding: een 6-tekens
 * leerlingcode kan nooit als docentcode gelden.
 */
export function generateCoTeacherCode(): string {
  return generateRandomCode(CO_TEACHER_CODE_LENGTH);
}

/**
 * Unieke co-docentcode. Botst met niets: we controleren tegen beide
 * codenamespaces, ook al maakt het lengteverschil een collisie al onmogelijk.
 * Die dubbele controle is er zodat een latere lengtewijziging niet stil een
 * overlap introduceert.
 */
export function generateUniqueCoTeacherCode(state: AccountsState): string {
  const taken = new Set<string>();
  for (const c of Object.values(state.classrooms)) {
    taken.add(c.joinCode);
    if (c.coTeacherCode) taken.add(c.coTeacherCode);
  }
  let code = generateCoTeacherCode();
  let attempts = 0;
  while (taken.has(code) && attempts < 100) {
    code = generateCoTeacherCode();
    attempts++;
  }
  return code;
}

// ── Classroom Queries ──

/**
 * Get all classrooms a teacher has access to — both owned (teacherId match)
 * AND shared (member of coTeacherIds). Owned classrooms come first, then
 * co-teacher classrooms (alphabetical within each group).
 *
 * Wave 3.1: Previously filtered only on teacherId, making co-teachers
 * invisible to themselves. Now includes both.
 */
export function getTeacherClassrooms(state: AccountsState, teacherId: string): Classroom[] {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return [];
  const owned: Classroom[] = [];
  const shared: Classroom[] = [];
  for (const c of Object.values(state.classrooms)) {
    if (c.teacherId === teacherId) {
      owned.push(c);
    } else if (c.coTeacherIds && c.coTeacherIds.includes(teacherId)) {
      shared.push(c);
    }
  }
  owned.sort((a, b) => a.name.localeCompare(b.name));
  shared.sort((a, b) => a.name.localeCompare(b.name));
  return [...owned, ...shared];
}

/**
 * Strict owner-only lookup. Use this for permission checks and CRUD
 * decisions where co-teacher access should NOT count.
 */
export function getOwnedClassrooms(state: AccountsState, teacherId: string): Classroom[] {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return [];
  return Object.values(state.classrooms).filter(c => c.teacherId === teacherId);
}

/**
 * Determine the role a teacher has within a classroom.
 * Returns null if no access.
 */
export function getClassroomRole(state: AccountsState, classroomId: string, teacherId: string): 'owner' | 'co-teacher' | null {
  const classroom = state.classrooms[classroomId];
  if (!classroom) return null;
  if (classroom.teacherId === teacherId) return 'owner';
  if (classroom.coTeacherIds && classroom.coTeacherIds.includes(teacherId)) {
    return 'co-teacher';
  }
  return null;
}

/** Maximum number of co-teachers per classroom (Wave 3.2 constraint). */
export const MAX_CO_TEACHERS_PER_CLASSROOM = 3;

/**
 * Find a classroom by its joinCode across all teachers.
 * Returns the classroom and its owning teacherId, or null.
 */
export function findClassroomByJoinCode(state: AccountsState, joinCode: string): {
  classroom: Classroom;
  teacherId: string;
} | null {
  const normalized = joinCode.toUpperCase().trim();
  if (!normalized || normalized.length < 4) return null;

  // Search top-level classroom registry (O(classrooms) instead of O(teachers × classrooms))
  const classroom = Object.values(state.classrooms).find(c => c.joinCode === normalized);
  if (classroom) {
    return {
      classroom,
      teacherId: classroom.teacherId
    };
  }
  return null;
}

/**
 * Zoek een klas op zijn CO-DOCENTCODE.
 *
 * Kijkt uitsluitend naar `coTeacherCode` en nooit naar `joinCode` — dat is de
 * hele reden dat deze functie apart bestaat. Een klas zonder co-docentcode is
 * dus niet te vinden, en dat is de bedoelde faalstand: geen code betekent dat
 * niemand co-docent kan worden.
 *
 * De lengte-eis is een goedkope zeef: wie hier een 6-tekens leerlingcode
 * invoert, krijgt gegarandeerd niets, ook niet bij toeval.
 */
export function findClassroomByCoTeacherCode(state: AccountsState, code: string): {
  classroom: Classroom;
  teacherId: string;
} | null {
  const normalized = code.toUpperCase().trim();
  if (normalized.length !== CO_TEACHER_CODE_LENGTH) return null;
  const classroom = Object.values(state.classrooms).find(c => c.coTeacherCode === normalized);
  if (!classroom) return null;
  return {
    classroom,
    teacherId: classroom.teacherId
  };
}

// ── Classroom Mutations ──

export function addClassroom(state: AccountsState, teacherId: string, classroom: Classroom): AccountsState {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return state;

  // Auto-generate joinCode and teacherId if empty/missing
  const enrichedClassroom: Classroom = {
    ...classroom,
    joinCode: classroom.joinCode || generateUniqueJoinCode(state),
    teacherId: classroom.teacherId || teacherId
  };
  return {
    ...state,
    // Write only to top-level classroom registry (canonical source)
    classrooms: {
      ...state.classrooms,
      [enrichedClassroom.id]: enrichedClassroom
    }
  };
}
export function updateClassroom(state: AccountsState, teacherId: string, classroomId: string, updates: Partial<Omit<Classroom, 'id' | 'createdAt' | 'learnerIds'>>): AccountsState {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return state;
  const existing = state.classrooms[classroomId];
  if (!existing || existing.teacherId !== teacherId) return state;
  const updatedClassroom = {
    ...existing,
    ...updates
  };
  return {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: updatedClassroom
    }
  };
}
export function removeClassroom(state: AccountsState, teacherId: string, classroomId: string): AccountsState {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return state;
  const existing = state.classrooms[classroomId];
  if (!existing || existing.teacherId !== teacherId) return state;
  const {
    [classroomId]: _removed,
    ...remainingClassrooms
  } = state.classrooms;

  // Clean up learnerToClassrooms reverse index for all learners in this classroom
  const updatedL2C = {
    ...state.relationships.learnerToClassrooms
  };
  const currentArchive = state.relationships.archivedLearnerLinks || {};
  let teacherArchive = currentArchive[teacherId] || [];
  let updatedTeacherToLearners = {
    ...state.relationships.teacherToLearners
  };
  let updatedLearnerToTeachers = {
    ...state.relationships.learnerToTeachers
  };
  for (const learnerId of existing.learnerIds) {
    const current = updatedL2C[learnerId] || [];
    if (current.includes(classroomId)) {
      updatedL2C[learnerId] = current.filter(id => id !== classroomId);
    }

    // Check if learner has any other classrooms with this teacher
    const hasOtherClassrooms = Object.values(remainingClassrooms).some(c => c.teacherId === teacherId && c.learnerIds.includes(learnerId));

    // If no other classrooms, we should archive them (soft-delete the teacher-learner link)
    if (!hasOtherClassrooms) {
      teacherArchive = [...teacherArchive, {
        learnerId,
        archivedAt: Date.now(),
        reason: 'classroom_delete',
        fromClassroomIds: [classroomId]
      }];

      // Remove the teacher-learner link
      updatedTeacherToLearners[teacherId] = (updatedTeacherToLearners[teacherId] || []).filter(id => id !== learnerId);
      updatedLearnerToTeachers[learnerId] = (updatedLearnerToTeachers[learnerId] || []).filter(id => id !== teacherId);
    }
  }
  return {
    ...state,
    classrooms: remainingClassrooms,
    relationships: {
      ...state.relationships,
      learnerToClassrooms: updatedL2C,
      teacherToLearners: updatedTeacherToLearners,
      learnerToTeachers: updatedLearnerToTeachers,
      archivedLearnerLinks: {
        ...currentArchive,
        [teacherId]: teacherArchive
      }
    }
  };
}
export function addLearnerToClassroom(state: AccountsState, teacherId: string, classroomId: string, learnerId: string): AccountsState {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return state;

  // Wave 3.1: Both owner and co-teachers may add learners. Reject if caller
  // has no access role on this classroom.
  const role = getClassroomRole(state, classroomId, teacherId);
  if (role === null) return state;

  // First ensure the caller's teacher-learner relationship exists
  let nextState = state;
  const currentLearners = state.relationships.teacherToLearners[teacherId] || [];
  if (!currentLearners.includes(learnerId)) {
    nextState = linkTeacherToLearner(state, teacherId, learnerId);
  }

  // Also link the OWNER + all OTHER co-teachers, so the learner is visible
  // to everyone with classroom access (not just the caller).
  const existingForLinks = nextState.classrooms[classroomId];
  if (existingForLinks) {
    const allTeacherIds = [existingForLinks.teacherId, ...(existingForLinks.coTeacherIds || [])];
    for (const tid of allTeacherIds) {
      if (tid === teacherId) continue;
      const tLearners = nextState.relationships.teacherToLearners[tid] || [];
      if (!tLearners.includes(learnerId)) {
        nextState = linkTeacherToLearner(nextState, tid, learnerId);
      }
    }
  }

  // Update classroom in top-level registry
  const existing = nextState.classrooms[classroomId];
  if (!existing || existing.learnerIds.includes(learnerId)) {
    // Classroom not found or learner already in it — just return with relationship update
    return nextState;
  }
  const updatedClassroom = {
    ...existing,
    learnerIds: [...existing.learnerIds, learnerId]
  };

  // Update learnerToClassrooms reverse index
  const currentL2C = nextState.relationships.learnerToClassrooms;
  const learnerClassrooms = currentL2C[learnerId] || [];
  const updatedL2C = learnerClassrooms.includes(classroomId) ? currentL2C : {
    ...currentL2C,
    [learnerId]: [...learnerClassrooms, classroomId]
  };
  return {
    ...nextState,
    classrooms: {
      ...nextState.classrooms,
      [classroomId]: updatedClassroom
    },
    relationships: {
      ...nextState.relationships,
      learnerToClassrooms: updatedL2C
    }
  };
}
export function removeLearnerFromClassroom(state: AccountsState, teacherId: string, classroomId: string, learnerId: string): AccountsState {
  const teacher = state.accountsById[teacherId];
  if (!teacher || teacher.role !== 'teacher') return state;

  // Wave 3.1: Both owner and co-teachers may remove learners from the
  // classroom. Reject if caller has no access role.
  const role = getClassroomRole(state, classroomId, teacherId);
  if (role === null) return state;

  // Update classroom in top-level registry
  const existing = state.classrooms[classroomId];
  if (!existing) return state;
  const updatedClassroom = {
    ...existing,
    learnerIds: existing.learnerIds.filter(id => id !== learnerId)
  };

  // Update learnerToClassrooms reverse index
  const currentL2C = state.relationships.learnerToClassrooms;
  const learnerClassrooms = (currentL2C[learnerId] || []).filter(id => id !== classroomId);
  const updatedL2C = {
    ...currentL2C,
    [learnerId]: learnerClassrooms
  };
  return {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: updatedClassroom
    },
    relationships: {
      ...state.relationships,
      learnerToClassrooms: updatedL2C
    }
  };
}
export function getClassroomStudents(state: AccountsState, teacherId: string, classroomId: string): LearnerAccount[] {
  const classrooms = getTeacherClassrooms(state, teacherId);
  const classroom = classrooms.find(c => c.id === classroomId);
  if (!classroom) return [];
  return classroom.learnerIds.map(id => state.accountsById[id]).filter((a): a is LearnerAccount => a?.role === 'learner');
}

/**
 * Join a classroom by joinCode. Adds the learner to the classroom
 * and creates the teacher-learner relationship if needed.
 * Returns the updated state and result info, or null if code not found.
 */
export function joinClassroomByCode(state: AccountsState, learnerId: string, joinCode: string): {
  state: AccountsState;
  classroomId: string;
  teacherId: string;
} | null {
  const found = findClassroomByJoinCode(state, joinCode);
  if (!found) return null;
  const {
    classroom,
    teacherId
  } = found;

  // Don't add if already in this classroom
  if (classroom.learnerIds.includes(learnerId)) {
    return {
      state,
      classroomId: classroom.id,
      teacherId
    };
  }

  // Use existing addLearnerToClassroom which also handles teacher-learner linking
  const nextState = addLearnerToClassroom(state, teacherId, classroom.id, learnerId);

  // Auto-sync learner grade from classroom groupLevel
  const learner = nextState.accountsById[learnerId];
  if (learner && learner.role === 'learner' && !learner.grade) {
    const updatedState: AccountsState = {
      ...nextState,
      accountsById: {
        ...nextState.accountsById,
        [learnerId]: {
          ...learner,
          grade: classroom.groupLevel
        }
      }
    };
    return {
      state: updatedState,
      classroomId: classroom.id,
      teacherId
    };
  }
  return {
    state: nextState,
    classroomId: classroom.id,
    teacherId
  };
}

/**
 * Regenerate the joinCode for a classroom.
 * Returns the updated state and the new code.
 */
export function regenerateJoinCode(state: AccountsState, teacherId: string, classroomId: string): {
  state: AccountsState;
  newCode: string;
} {
  const newCode = generateUniqueJoinCode(state);
  const updatedState = updateClassroom(state, teacherId, classroomId, {
    joinCode: newCode
  });
  return {
    state: updatedState,
    newCode
  };
}

/**
 * Maak of vervang de co-docentcode van een klas.
 *
 * Dient twee doelen met één handeling: aanmaken (klassen van vóór deze
 * wijziging hebben nog geen code) en intrekken (de oude werkt daarna niet
 * meer). Een echte "intrekken zonder vervangen" hoort er ook te komen —
 * contract C-9 — maar vervangen is hier de veilige kant: er is daarna altijd
 * precies één geldige code, nooit nul terwijl de UI er een toont.
 */
export function regenerateCoTeacherCode(state: AccountsState, teacherId: string, classroomId: string): {
  state: AccountsState;
  newCode: string;
} {
  const newCode = generateUniqueCoTeacherCode(state);
  const updatedState = updateClassroom(state, teacherId, classroomId, {
    coTeacherCode: newCode
  });
  return {
    state: updatedState,
    newCode
  };
}

// ── Validation & Guardrails ──────────────────────────────────
//
// NOTE — As of the linkage-domain unification, the canonical validator for
// adding a learner to a classroom lives in `learnerLinkage.ts`. The legacy
// `ClassroomValidationWarning` shape + `validateAddLearnerToClassroom` are
// kept here as a thin adapter for any remaining indirect callers. New code
// should import `validateLink()` from the accounts barrel directly.

export interface ClassroomValidationWarning {
  type: 'duplicate_name' | 'grade_mismatch' | 'grade_unknown' | 'already_in_other_classroom' | 'already_linked_to_other_teacher';
  message: string;
  details: {
    classroomName?: string;
    classroomId?: string;
    teacherName?: string;
    teacherId?: string;
    learnerGrade?: number;
    classroomGroupLevel?: number;
  };
}

/**
 * Check if a classroom name already exists (across ALL teachers).
 * Returns matching classrooms (excluding the one being edited, if excludeId provided).
 */
export function findDuplicateClassroomName(state: AccountsState, name: string, excludeId?: string): Array<{
  classroom: Classroom;
  teacherName: string;
}> {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return [];
  const duplicates: Array<{
    classroom: Classroom;
    teacherName: string;
  }> = [];
  for (const classroom of Object.values(state.classrooms)) {
    if (excludeId && classroom.id === excludeId) continue;
    if (classroom.name.trim().toLowerCase() === normalized) {
      const teacher = state.accountsById[classroom.teacherId];
      duplicates.push({
        classroom,
        teacherName: teacher?.displayName || 'Onbekend'
      });
    }
  }
  return duplicates;
}

/**
 * Get all classrooms a learner is currently enrolled in (across all teachers).
 * Returns classroom + teacher info for each.
 */
export function getLearnerExistingClassrooms(state: AccountsState, learnerId: string): Array<{
  classroom: Classroom;
  teacherName: string;
}> {
  const results: Array<{
    classroom: Classroom;
    teacherName: string;
  }> = [];
  for (const classroom of Object.values(state.classrooms)) {
    if (classroom.learnerIds.includes(learnerId)) {
      const teacher = state.accountsById[classroom.teacherId];
      results.push({
        classroom,
        teacherName: teacher?.displayName || 'Onbekend'
      });
    }
  }
  return results;
}

/**
 * Validate adding a learner to a classroom. Returns warnings (not errors —
 * the action can still proceed, but the user should be informed).
 *
 * Adapter — the canonical validator now lives in `learnerLinkage.ts`. This
 * function is a thin wrapper that maps the unified `LinkWarning` shape back
 * to the legacy `ClassroomValidationWarning` shape so existing callers keep
 * compiling unchanged. New code should call `validateLink()` directly.
 */
export function validateAddLearnerToClassroom(state: AccountsState, teacherId: string, classroomId: string, learnerId: string): ClassroomValidationWarning[] {
  // Lazy import to avoid an import cycle (learnerLinkage imports nothing
  // from classrooms.ts, but the static analyser is happier this way).
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const {
    validateLink
  } = require('./learnerLinkage') as typeof import('./learnerLinkage');
  const unified = validateLink(state, {
    kind: 'teacher-classroom',
    teacherId,
    classroomId,
    learnerId
  });
  const adapted: ClassroomValidationWarning[] = [];
  for (const w of unified) {
    switch (w.type) {
      case 'grade_mismatch':
        adapted.push({
          type: 'grade_mismatch',
          message: w.message,
          details: {
            learnerGrade: w.learnerGrade,
            classroomGroupLevel: w.classroomGroupLevel
          }
        });
        break;
      case 'grade_unknown':
        adapted.push({
          type: 'grade_unknown',
          message: w.message,
          details: {
            classroomGroupLevel: w.classroomGroupLevel
          }
        });
        break;
      case 'multi_classroom':
        adapted.push({
          type: 'already_in_other_classroom',
          message: w.message,
          details: {
            classroomName: w.classroomName,
            classroomId: w.classroomId,
            teacherName: w.teacherName,
            teacherId: w.teacherId
          }
        });
        break;
      case 'multi_teacher':
        adapted.push({
          type: 'already_linked_to_other_teacher',
          message: w.message,
          details: {
            teacherName: w.teacherName,
            teacherId: w.teacherId
          }
        });
        break;
      // multi_parent / already_linked_self only fire in the parent-learner
      // context and therefore cannot appear here. Listing them keeps the
      // switch exhaustive in spirit (TS doesn't enforce it on adapter shape).
      case 'multi_parent':
      case 'already_linked_self':
        break;
    }
  }
  return adapted;
}

/**
 * Validate creating a new classroom. Returns warnings about duplicate names.
 */
export function validateNewClassroom(state: AccountsState, name: string, teacherId: string): ClassroomValidationWarning[] {
  const warnings: ClassroomValidationWarning[] = [];
  const duplicates = findDuplicateClassroomName(state, name);
  for (const dup of duplicates) {
    const isSameTeacher = dup.classroom.teacherId === teacherId;
    warnings.push({
      type: 'duplicate_name',
      message: isSameTeacher ? `Je hebt al een klas met de naam "${dup.classroom.name}".` : `"${dup.classroom.name}" bestaat al bij ${dup.teacherName}. Wil je toch een klas met dezelfde naam aanmaken?`,
      details: {
        classroomName: dup.classroom.name,
        classroomId: dup.classroom.id,
        teacherName: dup.teacherName,
        teacherId: dup.classroom.teacherId
      }
    });
  }
  return warnings;
}

// ── School-Based Classroom Discovery ──

export interface ClassroomWithTeacher {
  classroom: Classroom;
  teacherName: string;
  teacherId: string;
  schoolName: string;
}

/**
 * Get ALL classrooms with teacher info. Useful as fallback when no same-school classrooms exist.
 */
export function getAllClassroomsWithTeachers(state: AccountsState): ClassroomWithTeacher[] {
  const results: ClassroomWithTeacher[] = [];
  for (const classroom of Object.values(state.classrooms)) {
    const teacher = state.accountsById[classroom.teacherId];
    const school = teacher && isTeacherAccount(teacher) ? teacher.schoolName || '' : '';
    results.push({
      classroom,
      teacherName: teacher?.displayName || 'Onbekend',
      teacherId: classroom.teacherId,
      schoolName: school
    });
  }
  return results.sort((a, b) => a.classroom.name.localeCompare(b.classroom.name));
}

/**
 * Get all classrooms belonging to a specific school (by schoolName on teachers).
 * Returns classrooms with teacher info, useful for "search existing classrooms" UIs.
 */
export function getSchoolClassrooms(state: AccountsState, schoolName: string): Array<{
  classroom: Classroom;
  teacherName: string;
  teacherId: string;
}> {
  if (!schoolName.trim()) return [];
  const normalized = schoolName.trim().toLowerCase();

  // Find all teacher IDs at this school
  const schoolTeacherIds = new Set<string>();
  for (const account of Object.values(state.accountsById)) {
    if (isTeacherAccount(account) && account.schoolName?.trim().toLowerCase() === normalized) {
      schoolTeacherIds.add(account.id);
    }
  }

  // Find all classrooms owned by those teachers
  const results: Array<{
    classroom: Classroom;
    teacherName: string;
    teacherId: string;
  }> = [];
  for (const classroom of Object.values(state.classrooms)) {
    if (schoolTeacherIds.has(classroom.teacherId)) {
      const teacher = state.accountsById[classroom.teacherId];
      results.push({
        classroom,
        teacherName: teacher?.displayName || 'Onbekend',
        teacherId: classroom.teacherId
      });
    }
  }
  return results.sort((a, b) => a.classroom.name.localeCompare(b.classroom.name));
}

/**
 * Transfer classroom ownership to a new teacher.
 * The old teacher loses ownership; the new teacher becomes the sole owner.
 *
 * Wave 3.1: If the new owner was previously a co-teacher of this classroom,
 * remove them from coTeacherIds (they're now the owner, not also a co-teacher).
 */
export function transferClassroomOwnership(state: AccountsState,
/**
 * Wie deze overdracht uitvoert. Alleen de huidige eigenaar mag dat.
 *
 * `null` betekent expliciet "systeemoperatie zonder aanvrager" en wordt
 * uitsluitend gebruikt bij het opheffen van een leerkrachtaccount, waar de
 * eigenaar juist degene is die verdwijnt. Die waarde moet je bewust
 * meegeven; hij is er niet om een controle te omzeilen.
 */
requesterId: string | null, classroomId: string, newTeacherId: string, keepAsCoTeacher: boolean = false): AccountsState {
  const classroom = state.classrooms[classroomId];
  if (!classroom) return state;
  const oldTeacherId = classroom.teacherId;

  // Deze functie draagt een hele klas — inclusief de koppeling aan élke
  // leerling erin — over aan iemand anders. Tot nu toe controleerde hij
  // uitsluitend of de klas bestond. De enige bescherming zat in het al dan
  // niet tonen van een knop, en een verborgen knop is geen rechtencontrole.
  if (requesterId !== null && oldTeacherId !== requesterId) {
    return state;
  }

  // De ontvanger moet ook echt een leerkracht zijn. Zonder deze controle kon
  // een klas worden overgedragen aan een ouder- of leerlingaccount, en dan
  // hangt er een klas aan iemand die er in geen enkel scherm bij kan.
  const receiver = state.accountsById[newTeacherId];
  if (!receiver || receiver.role !== 'teacher') return state;
  let updatedCoTeachers = (classroom.coTeacherIds || []).filter(id => id !== newTeacherId);
  if (keepAsCoTeacher && oldTeacherId && !updatedCoTeachers.includes(oldTeacherId)) {
    updatedCoTeachers = [...updatedCoTeachers, oldTeacherId];
  }

  // Update classroom teacherId
  let newState: AccountsState = {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: {
        ...classroom,
        teacherId: newTeacherId,
        coTeacherIds: updatedCoTeachers
      }
    }
  };

  // Link new teacher to all learners in the classroom
  for (const learnerId of classroom.learnerIds) {
    newState = linkTeacherToLearner(newState, newTeacherId, learnerId);
  }
  return newState;
}

/**
 * Join an existing classroom as co-teacher (mee-kijken).
 *
 * Wave 3.1: Now persists membership in `classroom.coTeacherIds` so the
 * classroom appears in the co-teacher's sidebar. Also links the co-teacher
 * to all learners via teacherToLearners (N:N relationship).
 *
 * Refuses to add:
 *   - the owner (already has full access)
 *   - duplicate entries (idempotent)
 *   - more than MAX_CO_TEACHERS_PER_CLASSROOM
 */
export function joinClassroomAsCoTeacher(state: AccountsState, teacherId: string, classroomId: string): AccountsState {
  const classroom = state.classrooms[classroomId];
  if (!classroom) return state;

  // Refuse self-invite (owner)
  if (classroom.teacherId === teacherId) return state;
  const currentCoTeachers = classroom.coTeacherIds || [];

  // Idempotent: already a co-teacher
  if (currentCoTeachers.includes(teacherId)) {
    // Still ensure learner-links are present (defensive)
    let newState = state;
    for (const learnerId of classroom.learnerIds) {
      newState = linkTeacherToLearner(newState, teacherId, learnerId);
    }
    return newState;
  }

  // Capacity guard
  if (currentCoTeachers.length >= MAX_CO_TEACHERS_PER_CLASSROOM) {
    return state;
  }

  // Add to coTeacherIds + link teacher to all learners in classroom
  let newState: AccountsState = {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: {
        ...classroom,
        coTeacherIds: [...currentCoTeachers, teacherId]
      }
    }
  };
  for (const learnerId of classroom.learnerIds) {
    newState = linkTeacherToLearner(newState, teacherId, learnerId);
  }
  return newState;
}

/**
 * Leave a classroom as co-teacher. Removes the teacher from `coTeacherIds`.
 * The owner cannot leave via this path — they must transfer or delete.
 *
 * Note: This does NOT unlink the teacher from learners — they may still be
 * linked via other classrooms or direct links. The teacher-learner unlink
 * is a separate explicit action.
 */
export function leaveClassroomAsCoTeacher(state: AccountsState, teacherId: string, classroomId: string): AccountsState {
  const classroom = state.classrooms[classroomId];
  if (!classroom) return state;
  if (classroom.teacherId === teacherId) return state; // owner cannot leave

  const currentCoTeachers = classroom.coTeacherIds || [];
  if (!currentCoTeachers.includes(teacherId)) return state;
  const nextState: AccountsState = {
    ...state,
    classrooms: {
      ...state.classrooms,
      [classroomId]: {
        ...classroom,
        coTeacherIds: currentCoTeachers.filter(id => id !== teacherId)
      }
    }
  };

  // Deze functie haalde tot nu toe alléén de naam uit `coTeacherIds` en liet
  // de leerlingkoppelingen staan die `joinClassroomAsCoTeacher` bij binnenkomst
  // had aangemaakt. Een vertrokken mede-leerkracht bleef daardoor élke leerling
  // van die klas zien: in Mijn Klas, in de dagoverzichten, in de rapporten en
  // in de CSV-export. De schermtekst beloofde het tegenovergestelde ("verliest
  // direct toegang tot de klas én de bijbehorende leerlingen").
  //
  // Toegang die blijft bestaan nadat iemand is verwijderd, is precies het
  // soort fout dat een verwerkersovereenkomst onhoudbaar maakt — en hij was
  // van buiten niet zichtbaar, want in de ledenlijst stond hij er netjes af.
  //
  // Een leerling wordt alleen ontkoppeld als deze leerkracht hem niet óók via
  // een andere klas kent. Anders zou het verlaten van klas A iemand ook zijn
  // eigen leerlingen in klas B afnemen.
  const stillReachable = new Set<string>();
  for (const other of Object.values(nextState.classrooms)) {
    if (other.id === classroomId) continue;
    const isOwner = other.teacherId === teacherId;
    const isCoTeacher = (other.coTeacherIds || []).includes(teacherId);
    if (!isOwner && !isCoTeacher) continue;
    for (const learnerId of other.learnerIds) stillReachable.add(learnerId);
  }
  let result = nextState;
  for (const learnerId of classroom.learnerIds) {
    if (stillReachable.has(learnerId)) continue;
    result = unlinkTeacherFromLearnerLink(result, teacherId, learnerId);
  }
  return result;
}

/**
 * Verbreekt uitsluitend de leerkracht↔leerling-koppeling.
 *
 * Bewust NIET `unlinkTeacherFromLearner` uit `relationships.ts`: die haalt de
 * leerling óók uit de klassen van deze leerkracht, en dat is hier verkeerd —
 * de leerling hoort bij de klas te blijven, alleen déze leerkracht hoort er
 * niet meer bij.
 */
function unlinkTeacherFromLearnerLink(state: AccountsState, teacherId: string, learnerId: string): AccountsState {
  const current = state.relationships.teacherToLearners[teacherId];
  if (!current || !current.includes(learnerId)) return state;
  return {
    ...state,
    relationships: {
      ...state.relationships,
      teacherToLearners: {
        ...state.relationships.teacherToLearners,
        [teacherId]: current.filter(id => id !== learnerId)
      }
    }
  };
}